/**
 * CreativeAsset — one picture a campaign can run: the bytes, what they
 * measure, and what Meta calls them once they have been uploaded there.
 *
 * Path:
 * `workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}/creativeAssets/{assetId}`
 * — an asset belongs to exactly one campaign and is stored under it, so
 * containment is the whole declaration of ownership and neither the campaign
 * nor the event is restated as a field. `Campaign.proposal.assetId` names one
 * of these documents; the launch callable is what resolves that id against
 * this collection, since no rule can follow a reference.
 *
 * Policy: read and list on the campaign's own predicate —
 * `tickets.ads.manage` + `mfa: true`, declared separately for the reason
 * `campaign.entity.ts` gives. NOTHING is client-writable at any tier. The
 * operator's own contribution arrives as bytes in Storage, and the trigger
 * that validates them is what writes the document, so `provenance` and
 * `imageHash` — the two fields whose truth the client is in no position to
 * assert, one a claim about who made the picture and the other a value only
 * Meta computes — are unforgeable rather than merely masked out.
 *
 * ── A POSTER REACHES A CAMPAIGN AS A CROP ─────────────────────────────────
 * A campaign artwork slot is 4:5 or 9:16 and every stored poster is 2:3
 * (`event.entity.ts`'s POSTER_RATIO), so the only 4:5 or 9:16 region of a
 * poster is a crop of it: new bytes, at the slot's own shape, framed by the
 * operator. That is `posterCrop`, and it is a different picture from the one
 * `eventPoster` names — that member carries the event's own live object at
 * the poster's own ratio, which no slot has room for. Two pictures, not two
 * copies of one, which is what earns the crop its own bytes.
 *
 * ── THE TWO CAPTIONS ARE BOUND TO PROVENANCE, AND NOT ALIKE ───────────────
 * `generationPrompt` and `fileName` each say what one source's picture is
 * called, and neither means anything on another's — so the refine below
 * forbids each outside the provenance it belongs to. Only the prompt is also
 * REQUIRED inside its own: it is composed here, so a generated asset without
 * one is a record this deployment failed to keep, and refusing it costs
 * nothing that was not already lost.
 *
 * A crop shows neither caption, and for two separate reasons: no file
 * arrived, so there is no name to render, and nobody sent words, so there is
 * no prompt to hand over. Both are refused below rather than left open.
 *
 * The name is not ours to require. It reaches the trigger as Storage object
 * metadata the browser set, and requiring it would answer a browser that
 * omitted it by refusing the whole document — trading a missing caption for
 * lost artwork. So it stays nullish, the trigger clamps and drops rather than
 * throwing, and an asset whose name could not be recovered renders no caption:
 * the honest state, not a false one.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

/**
 * The longest file name this model stores, and the bound the uploader clamps
 * to before it hands one over: 255 bytes is what a single name component may
 * be on the filesystems a file arrives from, so it is the largest a real name
 * can already be rather than a figure chosen here.
 */
export const CREATIVE_FILE_NAME_MAX_LENGTH = 255;

/**
 * The Storage object-metadata key the operator's own file name travels under —
 * the browser that uploads the bytes sets it, and the trigger that describes
 * them reads it. One declaration because two workspaces spell it: a second
 * copy is free to disagree, and a disagreement here is silent, since a key
 * nobody wrote reads as a file that simply had no name.
 *
 * The object's own name cannot carry it: the last path segment is the assetId
 * (`storagePath` below), which is what makes the upload addressable and
 * re-runnable.
 */
export const CREATIVE_FILE_NAME_METADATA_KEY = "fileName";

/**
 * The largest creative anyone may put under a campaign, mirroring
 * `storage.rules`' own `request.resource.size` condition — the rule is what
 * actually refuses an oversize upload. Here rather than beside either reader
 * for the reason the metadata key is: the browser checks it against
 * `File.size` before a byte leaves the device, `asset.webhook.ts` holds no
 * more than this in memory while it measures, and two copies of one figure
 * are free to disagree — a browser admitting what the rule refuses turns a
 * refusal the operator could have been told about into an opaque Storage
 * error. Five megabytes is the event poster's own budget (storage.rules'
 * poster block), and a campaign creative is the same kind of picture at the
 * same kind of size.
 *
 * MINUS ONE, and that is the whole of it: the rule bounds the size
 * EXCLUSIVELY (`size < 5 MiB`) while every reader of this constant bounds it
 * INCLUSIVELY — `<schmancy-dropzone>` refuses on `file.size > maxBytes`, the
 * trigger on `bytes.length > CREATIVE_MAX_BYTES`. So this is the largest size
 * the rule ADMITS rather than the first it refuses, which is what makes a
 * reader's own answer and the rule's the same answer at the boundary. Shift it
 * back to a round 5 MiB and a file of exactly that size passes the browser and
 * is refused by the bucket — the opaque error this constant exists to prevent,
 * arriving at the one size nobody thinks to test.
 */
export const CREATIVE_MAX_BYTES = 5 * 1024 * 1024 - 1;

// Where the picture came from — four peers, not a default with an escape
// hatch. Each is read off the channel the bytes travelled and never off the
// file, so a member is exactly as forgeable as the write that produced it.
//
//   member          written by                     the claim it makes
//   ──────────────  ─────────────────────────────  ────────────────────────
//   eventPoster     the server, pointing at the    these bytes ARE the
//                   event's own live object        event's poster
//   posterCrop      creative/posterCrop/{assetId}  the operator framed
//                                                  their own poster
//   operatorUpload  creative/upload/{assetId}      the operator brought
//                                                  this file
//   generatedHere   the server, from words it      this deployment made
//                   composed                       these bytes
//
// `storage.rules` grants a client those two prefixes and no other write
// anywhere under a campaign, so the prefix is the whole evidence and
// `asset.webhook.ts` stamps the member off it. A crop attests the weaker of
// the two poster claims on purpose: the operator chose the frame and the
// bytes are theirs, where "these bytes ARE the poster" stops being true the
// moment one pixel moves.
//
// The marking duty under Art. 50(2) attaches to synthetic output alone, so
// only `generatedHere` carries it and no client prefix can reach it:
// labelling a human artwork as AI-made is a false statement about authorship,
// and neither client member asserts anything about how a picture was made —
// only where it entered.
export const CreativeProvenanceSchema = z
	.enum(["eventPoster", "posterCrop", "operatorUpload", "generatedHere"])
	.describe(
		"where this picture came from — the event's own stored poster, a framing of that poster the operator composed, artwork the operator brought, or artwork generated here",
	);
export type CreativeProvenance = z.infer<typeof CreativeProvenanceSchema>;

// campaigns/{campaignId}/creativeAssets/{assetId}
// Fields justified:
//   provenance       — decides the marking duty, and nothing else does
//   width/height     — the source pixels, which the build re-measures from the
//                      bytes and refuses on disagreement, and which the
//                      console renders beside the picture
//   storagePath      — where the bytes are
//   fileName         — what the operator called the file they brought, so
//                      three uploads from one designer are three named tiles
//                      rather than three identical ones. Nothing else can
//                      answer it: `storagePath`'s last segment is the assetId
//                      this model issued, never the name the file arrived
//                      under, and a group heading identifies a set of one
//                      alone
//   imageHash        — what Meta calls these bytes; the build writes it here
//                      after the upload and hydrates from it on a retake, so
//                      one upload serves every re-entry
//   generationPrompt — the words we sent, shown to the operator so they can
//                      take them to their own designer and bring something back
// Excluded:
//   assetId / campaignId / eventId — the doc id and the path already carry
//     them; a field beside either is a second answer free to disagree
//   the aspect ratio — width and height already carry it, and a stored copy
//     is a second representation of one fact, free to contradict the two
//     fields it was reduced from. A reader wanting the shape in lowest terms
//     reduces the pair where it renders, for the same reason the verdict
//     below is not stored
//   a coverage verdict — derived from width/height at render time, never
//     stored, so one measurement cannot go stale against the picture it
//     describes
//   a caption the operator writes — the peer status the four sources hold is
//     what this field serves, and asking for a title before the picture is
//     even described turns bringing your own artwork into the longest of the
//     four paths rather than an equal one
//   the media type — `storagePath` names the bytes and the build re-measures
//     them from the container's own header, which is what it trusts; a stored
//     copy of what the uploader declared would be a second answer nothing reads
export const CreativeAssetSchema = z
	.object({
		provenance: CreativeProvenanceSchema,
		width: z
			.number()
			.int()
			.positive()
			.describe("the source picture's own pixels"),
		height: z
			.number()
			.int()
			.positive()
			.describe("the source picture's own pixels"),
		storagePath: z.string().min(1).describe("where the bytes live"),
		fileName: z
			.string()
			.min(1)
			.max(CREATIVE_FILE_NAME_MAX_LENGTH)
			.nullish()
			.describe(
				"the name the operator's own file arrived under, shown as the picture's caption — present on artwork the operator brought, and absent wherever the name could not be recovered",
			),
		imageHash: z
			.string()
			.nullish()
			.describe(
				"Meta's own hash for these bytes — absent until they have been uploaded there; byte-identical uploads return the same hash, which is what makes the upload re-runnable",
			),
		generationPrompt: z
			.string()
			.nullish()
			.describe(
				"the words this picture was generated from — present on generated artwork alone, and an output rather than an internal: it is shown and copyable",
			),
	})
	// `provenance` decides whether this field exists, which is ordinarily a
	// discriminated union — but no rule on this entity reads any field, so the
	// flat-shape exception the campaign files does not carry here, and the
	// refine is what keeps the two in step: a prompt beside human artwork
	// claims we wrote words nobody sent, and generated artwork without one
	// withholds the output the operator is meant to be able to take away.
	//
	// Both exclusions name the one provenance that owns their field and refuse
	// every other, so a member the enum grows shows neither caption from the
	// moment it exists — the safe default for a source nobody has yet decided
	// what to render for.
	.superRefine((asset, ctx) => {
		if (asset.provenance === "generatedHere" && asset.generationPrompt == null)
			ctx.addIssue({
				code: "custom",
				path: ["generationPrompt"],
				message: "generated artwork carries the words it was generated from",
			});
		if (asset.provenance !== "generatedHere" && asset.generationPrompt != null)
			ctx.addIssue({
				code: "custom",
				path: ["generationPrompt"],
				message: "artwork we did not generate has no prompt to show",
			});
		if (asset.provenance !== "operatorUpload" && asset.fileName != null)
			ctx.addIssue({
				code: "custom",
				path: ["fileName"],
				message: "only a file the operator brought arrives under a name",
			});
	});
export type CreativeAsset = z.infer<typeof CreativeAssetSchema>;

export const CreativeAsset = entity<Actor>()({
	schema: CreativeAssetSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.ads.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.ads.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}/creativeAssets/{assetId}",
		}),
	);
